// Generate Document — pick a matter, generate any template for it.
//
// Substep 5-π.3: this page used to be a second copy of the matters
// list whose only action was to navigate into MatterDetailPage, where
// the actual generator lived at the bottom of the Documents tab. The
// nav item said "Generate Doc" and delivered a list.
//
// Now the generator itself lives here, under a matter picker. The
// split is: the matter page's Documents tab is stage-driven (what
// should go out at this stage), this page is ad-hoc (I need this
// specific document for this client).
//
// `initialMatter` preselects the picker — the matter page links here
// with the matter you were already looking at, so the ad-hoc path is
// one click away rather than a hunt through the dropdown.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { C } from "../constants.js";
import { I } from "../icons.jsx";
import { cachedFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";
import { GenerateDocumentCard } from "../matter-detail/GenerateDocumentCard.jsx";

const label = (m) =>
  m ? `${m.client?.name || `Matter #${m.id}`}${m.display_number ? ` · ${m.display_number}` : ""}` : "";

export function GeneratePage({ onMatterClick, initialMatter = null, showNotification }) {
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(initialMatter);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    cachedFetch("/api/matters")
      .then(data => { setMatters(data.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Click-outside closes the dropdown. Without this the list stays
  // open behind the generator, which is a big surface to leave floating.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return matters;
    return matters.filter(m =>
      (m.client?.name || "").toLowerCase().includes(q)
      || (m.display_number || "").toLowerCase().includes(q)
      || String(m.id).includes(q));
  }, [matters, query]);

  const choose = (m) => {
    setSelected(m);
    setOpen(false);
    setQuery("");
  };

  return React.createElement("div", { className: "fade-in", style: { padding: "32px 40px", maxWidth: "1100px" } },
    React.createElement("h1", {
      style: { fontFamily: "'Playfair Display',serif", fontSize: "28px", fontWeight: 600, color: C.navy, marginBottom: "6px" },
    }, "Generate Document"),
    React.createElement("p", {
      style: { fontSize: "15px", color: C.textMuted, marginBottom: "20px" },
    }, "Pick a matter, then generate any template for it. For the documents a matter needs at its current stage, use the matter's Documents tab."),

    // ── Matter picker ───────────────────────────────────────────
    React.createElement("div", {
      ref: boxRef,
      style: { position: "relative", marginBottom: "20px", maxWidth: "560px" },
    },
      React.createElement("label", {
        style: {
          display: "block", fontSize: "11px", color: C.textLight, fontWeight: 600,
          textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px",
        },
      }, "Matter"),

      React.createElement("button", {
        onClick: () => setOpen(o => !o),
        disabled: loading,
        style: {
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: "10px", padding: "11px 14px", borderRadius: "10px",
          border: `1.5px solid ${selected ? C.gold : C.warmGray}`,
          background: "#fff", cursor: loading ? "wait" : "pointer",
          fontSize: "14px", color: selected ? C.navy : C.textMuted,
          fontWeight: selected ? 600 : 400, textAlign: "left",
        },
      },
        React.createElement("span", {
          style: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
        }, loading ? "Loading matters…" : selected ? label(selected) : "Select a matter…"),
        React.createElement("span", { style: { color: C.textLight, flexShrink: 0 } }, open ? "▲" : "▼"),
      ),

      open && React.createElement("div", {
        style: {
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50,
          background: "#fff", border: `1px solid ${C.warmGray}`, borderRadius: "10px",
          boxShadow: "0 12px 32px rgba(0,0,0,0.12)", overflow: "hidden",
        },
      },
        React.createElement("input", {
          type: "text", autoFocus: true, value: query,
          onChange: e => setQuery(e.target.value),
          placeholder: "Search by client or matter number…",
          style: {
            width: "100%", padding: "11px 14px", border: "none",
            borderBottom: `1px solid ${C.warmGray}`, fontSize: "13px", outline: "none",
          },
        }),
        React.createElement("div", { style: { maxHeight: "320px", overflowY: "auto" } },
          filtered.length === 0
            ? React.createElement("div", {
                style: { padding: "20px", textAlign: "center", color: C.textMuted, fontSize: "13px" },
              }, matters.length === 0 ? "No open matters found." : "No matters match that search.")
            : filtered.map((m, i) => React.createElement("button", {
                key: m.id,
                onClick: () => choose(m),
                style: {
                  width: "100%", display: "flex", alignItems: "center", gap: "12px",
                  padding: "11px 14px", border: "none", background: selected?.id === m.id ? `${C.gold}10` : "transparent",
                  cursor: "pointer", textAlign: "left",
                  borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}40`,
                },
                onMouseEnter: e => { e.currentTarget.style.background = C.warmWhite; },
                onMouseLeave: e => { e.currentTarget.style.background = selected?.id === m.id ? `${C.gold}10` : "transparent"; },
              },
                React.createElement("div", {
                  style: {
                    width: 32, height: 32, borderRadius: "50%", background: `${C.navy}10`, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: C.navy, fontFamily: "'Playfair Display',serif", fontSize: "14px", fontWeight: 600,
                  },
                }, (m.client?.name || "?")[0]),
                React.createElement("div", { style: { minWidth: 0 } },
                  React.createElement("div", {
                    style: { fontSize: "13px", fontWeight: 600, color: C.navy, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
                  }, m.client?.name || `Matter #${m.id}`),
                  React.createElement("div", { style: { fontSize: "11px", color: C.textMuted } }, m.display_number || ""),
                ),
              )),
        ),
      ),
    ),

    // ── Generator, or the empty state ───────────────────────────
    loading
      ? React.createElement("div", { style: { padding: "40px", textAlign: "center" } }, React.createElement(Spinner, null))
      : !selected
        ? React.createElement("div", {
            style: {
              background: "#fff", borderRadius: "12px", border: `1px dashed ${C.warmGray}`,
              padding: "48px 20px", textAlign: "center",
            },
          },
            React.createElement("div", { style: { color: C.textMuted, fontSize: "14px" } },
              "Select a matter above to choose a template."),
          )
        : React.createElement(React.Fragment, null,
            React.createElement("div", {
              style: { display: "flex", justifyContent: "flex-end", marginBottom: "10px" },
            },
              onMatterClick && React.createElement("button", {
                onClick: () => onMatterClick(selected),
                style: {
                  border: "none", background: "none", cursor: "pointer", padding: 0,
                  fontSize: "12px", fontWeight: 600, color: C.gold,
                  display: "flex", alignItems: "center", gap: "5px",
                },
              }, "Open this matter", I.chevron),
            ),
            React.createElement(GenerateDocumentCard, {
              key: selected.id,   // remount on matter change — no stale run state
              matter: selected,
              showNotification,
            }),
          ),
  );
}
